import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import {
  KeyboardEventType,
  KeyboardKey,
  MouseEventType,
  WindowEventType,
} from '@this+that/utils';

import { useShortcutsContext } from './ShortcutsContext';
import {
  isEditableTarget,
  isVisibleDialog,
  shouldIgnoreShortcut,
} from './shouldIgnoreShortcut';
import { ShortcutAction, ShortcutContext, ShortcutKey } from './types';
import { isTerminalTarget } from '../../components/Terminal/utils/terminalTarget';
import { APP_PAGES } from '../../routes/constants';

function sameShortcut(
  a: { key: ShortcutKey; contexts: ShortcutContext[] },
  b: { key: ShortcutKey; contexts: ShortcutContext[] },
): boolean {
  if (a.key !== b.key) {
    return false;
  }
  if (a.contexts.length !== b.contexts.length) {
    return false;
  }
  return a.contexts.every((c) => b.contexts.includes(c));
}

interface UseKeyboardShortcutsOptions {
  disableFocusSteal?: boolean;
}

export function useKeyboardShortcuts(
  options: UseKeyboardShortcutsOptions = {},
) {
  const location = useLocation();
  const {
    currentContext,
    setCurrentContext,
    selectedEmailIndex,
    setSelectedEmailIndex,
    isShortcutsEnabled,
    setIsShortcutsEnabled,
  } = useShortcutsContext();

  const actionsRef = useRef<ShortcutAction[]>([]);
  const keydownTargetWasInDialogRef = useRef(false);
  const keydownTargetWasEditableRef = useRef(false);

  useEffect(() => {
    const path = location.pathname;

    if (path === APP_PAGES.INBOX || path.startsWith(`${APP_PAGES.INBOX}?`)) {
      setCurrentContext(ShortcutContext.INBOX_LIST);
    } else if (
      path === APP_PAGES.INBOX_DRAFT ||
      path.startsWith(`${APP_PAGES.INBOX_DRAFT}?`)
    ) {
      setCurrentContext(ShortcutContext.DRAFT);
    } else if (
      path === APP_PAGES.INBOX_ALL ||
      path.startsWith(`${APP_PAGES.INBOX_ALL}?`)
    ) {
      setCurrentContext(ShortcutContext.ALL_MAIL);
    } else if (
      path === APP_PAGES.INBOX_SPAM ||
      path.startsWith(`${APP_PAGES.INBOX_SPAM}?`)
    ) {
      setCurrentContext(ShortcutContext.SPAM);
    } else if (
      path === APP_PAGES.INBOX_SENT ||
      path.startsWith(`${APP_PAGES.INBOX_SENT}?`)
    ) {
      setCurrentContext(ShortcutContext.SENT);
    } else if (
      path === APP_PAGES.INBOX_SEARCH ||
      path.startsWith(`${APP_PAGES.INBOX_SEARCH}?`)
    ) {
      setCurrentContext(ShortcutContext.SEARCH);
    } else if (
      path === APP_PAGES.INBOX_SNOOZED ||
      path.startsWith(`${APP_PAGES.INBOX_SNOOZED}?`)
    ) {
      setCurrentContext(ShortcutContext.SNOOZED);
    } else if (path.startsWith('/inbox/') && path !== APP_PAGES.INBOX) {
      setCurrentContext(ShortcutContext.INBOX_PREVIEW);
    } else if (path.startsWith('/do-box/preview/')) {
      setCurrentContext(ShortcutContext.INBOX_PREVIEW);
    } else if (
      path === APP_PAGES.DOBOX ||
      path.startsWith(`${APP_PAGES.DOBOX}?`)
    ) {
      setCurrentContext(ShortcutContext.DOBOX);
    } else {
      setCurrentContext(ShortcutContext.GLOBAL);
    }
  }, [location.pathname, setCurrentContext]);

  const registerShortcut = useCallback((action: ShortcutAction) => {
    actionsRef.current = [
      ...actionsRef.current.filter((a) => !sameShortcut(a, action)),
      action,
    ];
  }, []);

  const unregisterShortcut = useCallback(
    (key: ShortcutKey, contexts: ShortcutContext[]) => {
      actionsRef.current = actionsRef.current.filter(
        (a) => !sameShortcut(a, { key, contexts }),
      );
    },
    [],
  );

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (
        shouldIgnoreShortcut(event, {
          isShortcutsEnabled,
        })
      ) {
        return;
      }

      if (keydownTargetWasInDialogRef.current) {
        keydownTargetWasInDialogRef.current = false;
        return;
      }

      if (keydownTargetWasEditableRef.current) {
        keydownTargetWasEditableRef.current = false;
        return;
      }

      const key = event.key === KeyboardKey.SPACE ? 'Space' : event.key;

      const matchingActions = actionsRef.current.filter((action) => {
        // Single-letter shortcuts match case-insensitively (Caps Lock-safe) but only fire without Shift.
        // Shift+<letter> variants must be registered with shiftKey: true.
        const isSingleLetter = /^[a-zA-Z]$/.test(action.key);
        const caseInsensitiveMatch =
          action.key.toLowerCase() === key.toLowerCase();
        const exactMatch = action.key === key;

        const keyMatches = action.shiftKey
          ? caseInsensitiveMatch && event.shiftKey
          : isSingleLetter
            ? caseInsensitiveMatch && !event.shiftKey
            : exactMatch;

        const contextMatches =
          action.contexts.includes(currentContext) ||
          action.contexts.includes(ShortcutContext.GLOBAL);

        const shiftMatches = action.shiftKey ? event.shiftKey : true;
        const ctrlMatches = action.ctrlKey ? event.ctrlKey : true;
        const altMatches = action.altKey ? event.altKey : true;
        const metaMatches = action.metaKey ? event.metaKey : true;

        return (
          keyMatches &&
          contextMatches &&
          shiftMatches &&
          ctrlMatches &&
          altMatches &&
          metaMatches
        );
      });

      if (matchingActions.length > 0) {
        const action = matchingActions[0];

        if (action.preventDefault !== false) {
          event.preventDefault();
        }

        action.handler();
      }
    },
    [currentContext, isShortcutsEnabled],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      keydownTargetWasInDialogRef.current = Boolean(
        target?.closest?.('[role="dialog"]'),
      );
      keydownTargetWasEditableRef.current = Boolean(
        target && isEditableTarget(target),
      );
    };

    document.addEventListener(KeyboardEventType.KEYDOWN, handleKeyDown, true);
    document.addEventListener(KeyboardEventType.KEYUP, handleKeyUp);
    return () => {
      document.removeEventListener(
        KeyboardEventType.KEYDOWN,
        handleKeyDown,
        true,
      );
      document.removeEventListener(KeyboardEventType.KEYUP, handleKeyUp);
    };
  }, [handleKeyUp]);

  useEffect(() => {
    if (options.disableFocusSteal) {
      return;
    }

    if (isShortcutsEnabled) {
      const hasVisibleDialog = (): boolean => {
        const dialogs = document.querySelectorAll('[role="dialog"]');
        return Array.from(dialogs).some(isVisibleDialog);
      };

      const focusPage = (forceCheckbox = false) => {
        if (hasVisibleDialog()) {
          return;
        }

        const activeEl = document.activeElement as HTMLElement;
        if (
          activeEl?.closest?.('[data-message-panel="true"]') ||
          isTerminalTarget(activeEl)
        ) {
          return;
        }
        const tagName = activeEl?.tagName?.toLowerCase();
        const isInput =
          tagName === 'input' || tagName === 'textarea' || tagName === 'select';
        const isEditable =
          activeEl?.contentEditable === 'true' || activeEl?.isContentEditable;
        const isCheckbox =
          tagName === 'input' && activeEl?.getAttribute('type') === 'checkbox';

        const shouldFocus =
          (!isInput && !isEditable) || (forceCheckbox && isCheckbox);

        if (shouldFocus) {
          if (isCheckbox) {
            activeEl?.blur();
          }

          window.focus();
          if (document.body.tabIndex < 0) {
            document.body.tabIndex = -1;
          }
          document.body.focus();
        }
      };

      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const tagName = target?.tagName?.toLowerCase();

        if (tagName === 'input' && target.getAttribute('type') === 'checkbox') {
          setTimeout(() => {
            focusPage(true);
          }, 10);
        } else if (tagName === 'button' || target.closest('button')) {
          setTimeout(() => {
            focusPage();
          }, 10);
        }
      };

      focusPage();
      document.addEventListener(MouseEventType.CLICK, handleClick, true);

      const handleWindowFocus = () => focusPage();
      window.addEventListener(WindowEventType.FOCUS, handleWindowFocus);

      return () => {
        document.removeEventListener(MouseEventType.CLICK, handleClick, true);
        window.removeEventListener(WindowEventType.FOCUS, handleWindowFocus);
      };
    }
  }, [isShortcutsEnabled, options.disableFocusSteal]);

  return {
    registerShortcut,
    unregisterShortcut,
    currentContext,
    selectedEmailIndex,
    setSelectedEmailIndex,
    isShortcutsEnabled,
    setIsShortcutsEnabled,
  };
}
